import { Box, HStack, Text, Spinner, Center, IconButton } from "@chakra-ui/react";
import { Resizable } from "re-resizable";
import { useState } from "react";
import { FiX } from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import { useGetArtifact, useLunaStore } from "state/luna";
import { ArtifactContentRenderer } from "./artifactRenderers";

const MIN_WIDTH = 300;
const MAX_WIDTH = 1200;
const STORAGE_KEY = "luna:artifactViewerWidth";

const saveWidth = (width: number): void => {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    // localStorage not available
  }
};

export const ArtifactViewer = () => {
  const { sessionId, "*": wildcard } = useParams<{ sessionId?: string; "*"?: string }>();
  const navigate = useNavigate();

  // Parse artifact ID from wildcard path (e.g., "artifact/abc123" -> "abc123")
  const artifactMatch = wildcard?.match(/^artifact\/(.+)$/);
  const artifactId = artifactMatch ? artifactMatch[1] : null;

  const { data: artifact, isLoading } = useGetArtifact(artifactId);
  // Use local state for resize performance - read initial value without subscribing
  const [width, setWidth] = useState(() => useLunaStore.getState().artifactViewerWidth);

  const handleClose = () => {
    if (sessionId) {
      navigate(`/luna/${sessionId}`);
    }
  };

  if (!artifactId) {
    return null;
  }

  return (
    <Resizable
      size={{ width, height: "100%" }}
      onResize={(_e, _direction, ref) => {
        setWidth(ref.offsetWidth);
      }}
      onResizeStop={(_e, _direction, ref) => {
        saveWidth(ref.offsetWidth);
      }}
      minWidth={MIN_WIDTH}
      maxWidth={MAX_WIDTH}
      enable={{
        top: false,
        right: false,
        bottom: false,
        left: true,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      handleStyles={{
        left: {
          width: "6px",
          left: 0,
          cursor: "col-resize",
        },
      }}
      handleComponent={{
        left: (
          <Box
            h="100%"
            w="6px"
            bg="transparent"
            _hover={{ bg: "p.200" }}
            transition="background 0.15s"
          />
        ),
      }}
      style={{ flexShrink: 0 }}
    >
      <Box
        h="100%"
        bg="bg.app"
        borderLeft="1px solid"
        borderColor="border.default"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Header */}
        <HStack
          h="64px"
          flexShrink={0}
          spacing="lg"
          px="lg"
          bg="bg.page"
          borderBottom="1px solid"
          borderColor="border.default"
        >
          <Text
            flex={1}
            fontSize="md"
            fontWeight="medium"
            color="text.body.primary"
            noOfLines={1}
          >
            {artifact?.title || "Loading..."}
          </Text>
          <IconButton
            aria-label="Close artifact viewer"
            icon={<FiX size={16} />}
            variant="ghost"
            size="sm"
            onClick={handleClose}
            color="text.body.tertiary"
            minW="auto"
            p="sm"
          />
        </HStack>

        {/* Content */}
        <Box
          flex={1}
          overflowY={artifact?.type === "subagent" ? "hidden" : "auto"}
          p={artifact?.type === "subagent" ? 0 : "lg"}
        >
          {isLoading ? (
            <Center py="xl">
              <Spinner size="lg" color="p.600" />
            </Center>
          ) : artifact ? (
            <ArtifactContentRenderer artifact={artifact} />
          ) : (
            <Center py="xl">
              <Text color="text.body.tertiary">Artifact not found</Text>
            </Center>
          )}
        </Box>
      </Box>
    </Resizable>
  );
};
